import { FC, useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import { Button, Field, Stack, Text } from '@chakra-ui/react';

import { useUploadApplicationDoc } from '~/core/shared/api/middle-office/application/hooks/useUploadApplicationDoc';
import {
  Dialog,
  FileUpload,
  FormField,
  FormLabel,
  Select,
} from '~/core/shared/components';
import { DocStatusEnum, EMPTY_ARR } from '~/core/shared/constants';
import { useFormResolver } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Doc, DocsUploadFormValues } from '~/core/shared/types';
import { handleError } from '~/core/shared/utils/admin';
import { DocsTabContext } from '../application/tabs';

type FileObject = {
  file: File;
  id: string;
};

type DocUploadModalProps = {
  doc: Doc;
};

const docStatusOptions = Object.values(DocStatusEnum).map((type) => ({
  label: type,
  value: type,
}));

const validationSchema = yup.object({
  status: yup.string().oneOf(Object.values(DocStatusEnum)).required(),
});

export const DocUploadModal: FC<DocUploadModalProps> = ({ doc }) => {
  const { uuid, definition, docUploadModalApi, docTypes } =
    useContext(DocsTabContext);
  const { uploadDoc, isPending } = useUploadApplicationDoc(definition);
  const [fileObjects, setFileObjects] = useState<FileObject[]>(EMPTY_ARR);

  // TODO: @haxxxton refactor to use form blocks
  const resolver = useFormResolver<DocsUploadFormValues>(validationSchema);
  const form = useForm<DocsUploadFormValues>({
    resolver,
  });

  const onSubmit = useEventHandler(
    async (formValues: DocsUploadFormValues): Promise<void> => {
      if (fileObjects.length === 0) {
        handleError('Please choose a file');
        return;
      }

      await uploadDoc({
        uuid,
        data: {
          files: [{ file: fileObjects[0].file, key: 'file' }],
          json: formValues,
        },
      });

      form.reset();
      setFileObjects(EMPTY_ARR);
      docUploadModalApi.onClose();
    },
  );

  return (
    <Dialog
      header="Upload a new document"
      onOpenChange={docUploadModalApi.onOpenChange}
      open={docUploadModalApi.open}
      size="lg"
      subHeader="Upload attachment attached to the application"
    >
      <FormProvider {...form}>
        <Stack as="form" gap={6} onSubmit={form.handleSubmit(onSubmit)}>
          <Text fontWeight="semibold">{doc.type}</Text>

          <Field.Root>
            <FormLabel htmlFor="file-upload">File upload</FormLabel>
            <FileUpload
              name="file-upload"
              onBlur={() => {}}
              onChange={() => {}}
              onRemoveCallback={async (fileResource) =>
                setFileObjects((value) =>
                  value.filter((v) => v.id !== fileResource.id),
                )
              }
              onUploadCallback={async (file) => {
                const newFileObject = {
                  file,
                  id: uuidv4(),
                };

                setFileObjects((value) => [...value, newFileObject]);

                return {
                  filename: file.name,
                  id: newFileObject.id,
                };
              }}
              value={fileObjects.map(({ id, file }) => ({
                filename: file.name,
                id,
              }))}
            />
          </Field.Root>

          <FormField
            borderColor="gray.300"
            component={Select}
            label="Status"
            name="status"
            options={docStatusOptions}
            required
            size="lg"
            textTransform="capitalize"
            variant="outline"
          />

          <Stack direction="row" justifyContent="end">
            <Button
              colorPalette="gray"
              fontSize="md"
              fontWeight="normal"
              onClick={docUploadModalApi.onClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              colorPalette="primary"
              fontSize="md"
              fontWeight="bold"
              loading={isPending}
              type="submit"
              variant="solid"
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </Dialog>
  );
};
